import React, { useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { Input, Typography, DatePicker, Button, App } from "antd";
import { SearchOutlined, DeleteOutlined } from "@ant-design/icons";
const { Text } = Typography;
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";

import { VirtualKeyboard } from "../../shared/components";
import { useAntdTheme } from "../../shared/hooks";

interface ResultsLocationState {
  query?: string;
  orderDate?: string | null;
  fromResults?: boolean;
}

const OrderLookupSearch: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();

  const restaurantId = sessionStorage.getItem("restaurant_id");
  if (!restaurantId) {
    return <Navigate to="/login" replace />;
  }

  // Restore query/date if we are returning from the results page.
  const incomingState = location.state as ResultsLocationState | null;
  const [query, setQuery] = useState<string>(incomingState?.query ?? "");
  const [orderDate, setOrderDate] = useState<Dayjs | null>(
    incomingState?.orderDate ? dayjs(incomingState.orderDate) : null
  );

  const onSearch = () => {
    if (!query.trim()) {
      notification.warning({
        message: t("warning"),
        description: t("input_search_text"),
      });
      return;
    }

    navigate("/order-lookup/results", {
      state: {
        query,
        orderDate: orderDate ? orderDate.format() : null,
      },
    });
  };

  const onClear = () => setQuery("");

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        flex: 1,
        minHeight: 0,
        gap: token.marginMD,
      }}
    >
      {/* Top section: 3-column grid.
          Col 1: input (row 1) + date picker (row 2)
          Col 2: Clear button (spans both rows)
          Col 3: Search button (spans both rows) */}
      <div
        style={{
          flex: "0 0 auto",
          display: "grid",
          gridTemplateColumns: "1fr auto auto",
          gridTemplateRows: "auto auto",
          gap: token.marginSM,
          alignItems: "stretch",
        }}
      >
        {/* Row 1, Col 1: query input */}
        <Input
          value={query}
          placeholder={t("input_search_text")}
          readOnly
          size="large"
          suffix={
            <SearchOutlined
              style={{ color: token.colorTextTertiary, fontSize: token.fontSizeLG }}
            />
          }
          style={{
            borderRadius: token.borderRadiusLG,
            fontSize: token.fontSizeLG,
          }}
        />

        {/* Col 2: Clear button (spans both rows) */}
        <Button
          danger
          icon={<DeleteOutlined style={{ fontSize: token.fontSizeLG }} />}
          onClick={onClear}
          disabled={!query}
          style={{
            gridColumn: 2,
            gridRow: "1 / span 2",
            height: "100%",
            minHeight: 96,
            borderRadius: token.borderRadiusLG,
            fontSize: token.fontSizeLG,
            fontWeight: token.fontWeightStrong,
          }}
        >
          {t("clear")}
        </Button>

        {/* Col 3: Search button (spans both rows) */}
        <Button
          type="primary"
          icon={<SearchOutlined style={{ fontSize: token.fontSizeLG }} />}
          onClick={onSearch}
          disabled={!query.trim()}
          style={{
            gridColumn: 3,
            gridRow: "1 / span 2",
            height: "100%",
            minHeight: 96,
            borderRadius: token.borderRadiusLG,
            fontSize: token.fontSizeLG,
            fontWeight: token.fontWeightStrong,
          }}
        >
          {t("search")}
        </Button>

        {/* Row 2, Col 1: date picker */}
        <DatePicker
          value={orderDate}
          placeholder={t("order_date_optional")}
          format="ddd, MMM DD"
          disabledDate={(current) => current && current.isAfter(dayjs())}
          onChange={setOrderDate}
          allowClear
          size="large"
          style={{
            width: "100%",
            borderRadius: token.borderRadiusLG,
          }}
        />
      </div>

      {/* Keyboard fills the remaining height, 90% width centered */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          width: "90%",
          margin: "0 auto",
        }}
      >
        <VirtualKeyboard
          value={query}
          onChange={setQuery}
          onSubmit={onSearch}
          showSubmit={false}
          showClear={false}
        />
      </div>

      {/* Hint (fixed height) */}
      <div style={{ flex: "0 0 auto", textAlign: "center" }}>
        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          {t("virtual_keyboard_hint")}
        </Text>
      </div>
    </div>
  );
};

export default OrderLookupSearch;
